import React, { useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Invoice Selection Modal Component
 * Provides options for Standard and Flexible invoice generation
 */
function InvoiceSelectionModal({ 
  isOpen, 
  onClose, 
  booking, 
  onDownloadStandard, 
  onDownloadFlexible,
  downloading = false 
}) {
  // State for flexible invoice options
  const [flexibleOptions, setFlexibleOptions] = useState({
    includeRoom: true,
    includeCourse: true,
    includeAddons: true,
    includePayments: true
  });

  // State for company details (optional)
  const [companyDetails, setCompanyDetails] = useState({
    companyName: '',
    gstNumber: ''
  });

  // Handle checkbox changes for flexible options
  const handleFlexibleOptionChange = (option) => {
    setFlexibleOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  // Handle company details changes
  const handleCompanyDetailsChange = (field, value) => {
    setCompanyDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Validate flexible options (at least one service must be selected)
  const isFlexibleOptionsValid = () => {
    return flexibleOptions.includeRoom || 
           flexibleOptions.includeCourse || 
           flexibleOptions.includeAddons;
  };

  // Handle standard invoice download
  const handleStandardDownload = () => {
    // Include company details if provided
    const params = {};
    if (companyDetails.companyName.trim()) {
      params.companyName = companyDetails.companyName.trim();
    }
    if (companyDetails.gstNumber.trim()) {
      params.gstNumber = companyDetails.gstNumber.trim();
    }
    onDownloadStandard(booking.id, params);
  };

  // Handle flexible invoice download
  const handleFlexibleDownload = () => {
    if (!isFlexibleOptionsValid()) {
      toast.error('Please select at least one service (Room, Course, or Addons) for the flexible invoice.');
      return;
    }
    // Include company details if provided
    const options = { ...flexibleOptions };
    if (companyDetails.companyName.trim()) {
      options.companyName = companyDetails.companyName.trim();
    }
    if (companyDetails.gstNumber.trim()) {
      options.gstNumber = companyDetails.gstNumber.trim();
    }
    onDownloadFlexible(booking.id, options);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#E9DFCE',
        padding: '30px',
        borderRadius: '12px',
        width: '500px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px',
          paddingBottom: '15px',
          borderBottom: '2px solid #EEEBE1'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '700',
            color: '#1A3E2A',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            Invoice Options
          </h2>
          <button
            onClick={onClose}
            disabled={downloading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: downloading ? 'not-allowed' : 'pointer',
              color: '#5F6B61',
              padding: '5px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Booking Info */}
        <div style={{
          backgroundColor: '#F5F3EB',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '25px',
          border: '1px solid #E3DFD3'
        }}>
          <h3 style={{
            margin: '0 0 10px 0',
            fontSize: '16px',
            fontWeight: '600',
            color: '#36433B'
          }}>
            Booking Details
          </h3>
          <div style={{ fontSize: '14px', color: '#5F6B61' }}>
            <div><strong>Booking #:</strong> {booking?.bookingNumber || 'N/A'}</div>
            <div><strong>Guest:</strong> {booking?.user?.name || 'N/A'}</div>
            <div><strong>Total Amount:</strong> ₹{booking?.totalPrice || 0}</div>
          </div>
        </div>

        {/* Company Details Section (Optional) */}
        <div style={{
          backgroundColor: '#FAF4E8',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '25px',
          border: '1px solid #D8B26A'
        }}>
          <h3 style={{
            margin: '0 0 5px 0',
            fontSize: '16px',
            fontWeight: '600',
            color: '#36433B',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            Company Details
            <span style={{
              fontSize: '11px',
              fontWeight: '500',
              color: '#9E712E',
              backgroundColor: '#F3E6CB',
              padding: '2px 8px',
              borderRadius: '12px'
            }}>
              Optional
            </span>
          </h3>
          <p style={{
            margin: '5px 0 15px 0',
            fontSize: '12px',
            color: '#5F6B61'
          }}>
            Add company details for business/corporate bookings (GST compliance)
          </p>
          
          {/* Company Name Input */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#36433B',
              marginBottom: '6px'
            }}>
              Company Name
            </label>
            <input
              type="text"
              placeholder="e.g., ABC Corporation"
              value={companyDetails.companyName}
              onChange={(e) => handleCompanyDetailsChange('companyName', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#1A3E2A'}
              onBlur={(e) => e.target.style.borderColor = '#D2CDBF'}
            />
          </div>

          {/* GST Number Input */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#36433B',
              marginBottom: '6px'
            }}>
              GST Number
            </label>
            <input
              type="text"
              placeholder="e.g., 07AABCS9603R1Z5"
              value={companyDetails.gstNumber}
              onChange={(e) => handleCompanyDetailsChange('gstNumber', e.target.value.toUpperCase())}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'monospace'
              }}
              onFocus={(e) => e.target.style.borderColor = '#1A3E2A'}
              onBlur={(e) => e.target.style.borderColor = '#D2CDBF'}
            />
          </div>
        </div>

        {/* Invoice Format Options */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{
            margin: '0 0 15px 0',
            fontSize: '18px',
            fontWeight: '600',
            color: '#36433B'
          }}>
            Select Invoice Format
          </h3>

          {/* Standard Invoice Option */}
          <div style={{
            border: '2px solid #E3DFD3',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '15px',
            backgroundColor: '#ffffff'
          }}>
            <h4 style={{
              margin: '0 0 8px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: '#163626',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Standard Invoice
            </h4>
            <p style={{
              margin: '0 0 15px 0',
              fontSize: '14px',
              color: '#5F6B61',
              lineHeight: '1.5'
            }}>
              Complete invoice with all services (Room, Course, Addons) and payment details included.
            </p>
            <button
              onClick={handleStandardDownload}
              disabled={downloading}
              style={{
                backgroundColor: downloading ? '#A5A89B' : '#163626',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: downloading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {downloading ? (
                <>
                  <span style={{ 
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    border: '2px solid #ffffff40',
                    borderTop: '2px solid #ffffff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Downloading...
                </>
              ) : (
                <>
                  Download Standard Invoice
                </>
              )}
            </button>
          </div>

          {/* Flexible Invoice Option */}
          <div style={{
            border: '2px solid #E3DFD3',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#ffffff'
          }}>
            <h4 style={{
              margin: '0 0 8px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1A3E2A',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Flexible Invoice
            </h4>
            <p style={{
              margin: '0 0 15px 0',
              fontSize: '14px',
              color: '#5F6B61',
              lineHeight: '1.5'
            }}>
              Customizable invoice with selective service inclusion and payment details.
            </p>

            {/* Service Selection */}
            <div style={{ marginBottom: '20px' }}>
              <h5 style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#36433B'
              }}>
                Include Services:
              </h5>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Room Service */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={flexibleOptions.includeRoom}
                    onChange={() => handleFlexibleOptionChange('includeRoom')}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ color: '#36433B' }}>
                    <strong>Room/Accommodation</strong> - Room charges and accommodation fees
                  </span>
                </label>

                {/* Course Service */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={flexibleOptions.includeCourse}
                    onChange={() => handleFlexibleOptionChange('includeCourse')}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ color: '#36433B' }}>
                    <strong>Course Fees</strong> - Course registration and enrollment charges
                  </span>
                </label>

                {/* Addon Services */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={flexibleOptions.includeAddons}
                    onChange={() => handleFlexibleOptionChange('includeAddons')}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ color: '#36433B' }}>
                    <strong>Additional Services</strong> - Meals, activities, and other addons
                  </span>
                </label>

                {/* Payment Details */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={flexibleOptions.includePayments}
                    onChange={() => handleFlexibleOptionChange('includePayments')}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ color: '#36433B' }}>
                    <strong>Payment Details</strong> - Payment history and transaction information
                  </span>
                </label>
              </div>
            </div>

            {/* Validation Message */}
            {!isFlexibleOptionsValid() && (
              <div style={{
                backgroundColor: '#F9EEEB',
                border: '1px solid #E5B3A9',
                color: '#8E3628',
                padding: '10px',
                borderRadius: '6px',
                fontSize: '13px',
                marginBottom: '15px'
              }}>
                Please select at least one service (Room, Course, or Addons) to generate the invoice.
              </div>
            )}

            {/* Download Button */}
            <button
              onClick={handleFlexibleDownload}
              disabled={downloading || !isFlexibleOptionsValid()}
              style={{
                backgroundColor: downloading || !isFlexibleOptionsValid() ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: downloading || !isFlexibleOptionsValid() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {downloading ? (
                <>
                  <span style={{ 
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    border: '2px solid #ffffff40',
                    borderTop: '2px solid #ffffff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Downloading...
                </>
              ) : (
                <>
                  Download Flexible Invoice
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: '15px',
          borderTop: '1px solid #E3DFD3'
        }}>
          <button
            onClick={onClose}
            disabled={downloading}
            style={{
              backgroundColor: 'transparent',
              color: '#5F6B61',
              border: '1px solid #D2CDBF',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: downloading ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default InvoiceSelectionModal;
